import type { Branding } from "@/api/branding";

/**
 * Host-keyed localStorage cache of the last resolved dealer brand, used to kill
 * the flash-of-unbranded-content (FOUC) on reload (BUG-245).
 *
 * The brand is normally applied only after an async fetch resolves (post first
 * paint), so a branded page briefly shows the platform default on every reload.
 * We persist the resolved brand here and re-apply the CSS vars synchronously
 * from an inline `<script>` in `index.html` BEFORE React mounts. This module is
 * the React-side read/write half; the inline script duplicates a minimal read
 * (it cannot import) — keep {@link BRAND_CACHE_PREFIX} and the stored shape in
 * sync with `index.html`.
 *
 * Keyed by `window.location.host` because custom-domain brand is host-resolved
 * and one browser may hit several hosts (platform domain + dealer domains).
 */
export const BRAND_CACHE_PREFIX = "brand_cache:";

/** The subset of {@link Branding} persisted for pre-paint application. */
export interface CachedBrand {
  brand_name: string;
  logo_url: string;
  logo_dark_url: string;
  favicon_url: string;
  primary: string;
  sidebar: string;
  accent: string;
}

function cacheKey(): string {
  return `${BRAND_CACHE_PREFIX}${window.location.host}`;
}

/**
 * Persist a resolved NON-default brand for the current host. An `is_default`
 * brand clears the key instead, so a host that stops being branded does not
 * keep flashing stale colors on the next reload.
 */
export function writeCachedBrand(brand: Branding): void {
  try {
    if (brand.is_default) {
      localStorage.removeItem(cacheKey());
      return;
    }
    const payload: CachedBrand = {
      brand_name: brand.brand_name,
      logo_url: brand.logo_url,
      logo_dark_url: brand.logo_dark_url,
      favicon_url: brand.favicon_url,
      primary: brand.colors.primary,
      sidebar: brand.colors.sidebar,
      accent: brand.colors.accent,
    };
    localStorage.setItem(cacheKey(), JSON.stringify(payload));
  } catch {
    // Best-effort: a full/blocked localStorage must never break branding.
  }
}

/** Read the cached brand for the current host, or `null` when absent/invalid. */
export function readCachedBrand(): CachedBrand | null {
  try {
    const raw = localStorage.getItem(cacheKey());
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as CachedBrand;
  } catch {
    return null;
  }
}
